import React, { useEffect, useMemo, useState } from 'react';
import { useAPI } from '@arema/components/Hooks';
import { EventPaymentSources } from '@arema/types/internal';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Dropdown, Field, Icon, Input, Table } from 'react-frontier2';
import { Header } from 'react-frontier2';
import { addCommas, removeAccents } from '@arema/components/Util';
import API from '../../API';

interface OriginsDayProps{
	month?: boolean,
}

enum SystemFilter{
	ALL = 1,
	T2 = 2,
	T3 = 3,
}

var OriginsDay = (props: OriginsDayProps)=>{
	var { t } = useTranslation();
	var { day } = useParams<{ day: string }>();
	var { data, load, loadError } = useAPI<EventPaymentSources[]>(API.getDateDepositSources);
	var [businessFilter, setBusinessFilter] = useState<number>(null);
	var [systemFilter, setSystemFilter] = useState<number>(SystemFilter.ALL);
	var [eventSearch, setEventSearch] = useState<string>(null);

	var { date_start, date_end } = useMemo(()=>{
		var date_start : DateTime = null, date_end: DateTime = null;
		if(day){
			if(props.month){
				date_start = DateTime.fromFormat(day, 'MM-yyyy').startOf('month');
			}else{
				date_start = DateTime.fromFormat(day, 'yyyy-MM-dd').startOf('day');
			}
			if(date_start.isValid){
				date_end = date_start.endOf(props.month ? 'month' : 'day');
			}else{
				date_start = null;
				date_end = null;
			}
		}
		return { date_start, date_end };
	}, [day]);

	useEffect(()=>{
		if(!date_start) return;
		if(!data){
			load(date_start.toSeconds(), date_end.toSeconds(), (systemFilter || 2)-1);
		}
	}, [date_start, date_end]);

	var data_summary = useMemo(()=>{
		if(!data) return null;
		var ev : {
			event_id: number,
			event_name: string,
			ticket_cost: number,
			commission: number,
		}[] = [];
		for(var i of data){
			if(eventSearch && eventSearch.length>1){
				if(removeAccents(i.event_name.toLowerCase()).indexOf(removeAccents(eventSearch.toLowerCase()))==-1){
					continue;
				}
			}
			var e = {
				event_id: i.event_id,
				event_name: i.event_name,
				ticket_cost: 0,
				commission: 0,
			}
			for(var b of i.business){
				if(!businessFilter || businessFilter==-1 || (b.business_id===businessFilter)){
					for(var s of b.sources){
						switch(systemFilter){
							case SystemFilter.ALL:
								e.ticket_cost += s.ticket_cost;
								e.commission += s.commission;
								break;
							case SystemFilter.T2:
								e.ticket_cost += s.legacy.ticket_cost;
								e.commission += s.legacy.commission;
								break;
							case SystemFilter.T3:
								e.ticket_cost += s.ticket_cost-s.legacy.ticket_cost;
								e.commission += s.commission-s.legacy.commission;
								break;
						}
					}
				}
			}
			ev.push(e);
		}
		return ev.filter(a=>a.ticket_cost>0 || a.commission>0);
	}, [data, eventSearch, businessFilter, systemFilter]);

	var { sources, business, totals } = useMemo(()=>{
		var sources : {
			source_id: number,
			source_name: string,
			ticket_cost: number,
			commission: number,
		}[] = [];
		var business : {
			business_id: number,
			business_name: string,
			ticket_cost: number,
			commission: number,
		}[] = [];

		var totals : {
			ticket_cost: number,
			commission: number,
		} = {
			ticket_cost: 0,
			commission: 0,
		}
		
		if(!!data){
			for(var i of data){
				if(eventSearch && eventSearch.length>1){
					if(removeAccents(i.event_name.toLowerCase()).indexOf(removeAccents(eventSearch.toLowerCase()))==-1){
						continue;
					}
				}
				for(var b of i.business){
					if(businessFilter && businessFilter>0 && businessFilter!==b.business_id){
						continue;
					}
					var bx = business.findIndex(a=>a.business_id===b.business_id);
					if(bx===-1){
						bx = business.push({
							business_id: b.business_id,
							business_name: b.business_name,
							ticket_cost: 0,
							commission: 0,
						})-1;
					}
					
					for(var s of b.sources){
						var sx = sources.findIndex(a=>a.source_id===s.source_id);
						if(sx===-1){
							sx = sources.push({
								source_id: s.source_id,
								source_name: s.source_name,
								ticket_cost: 0,
								commission: 0,
							})-1;
						}

						var tc : number, cm : number;
						switch(systemFilter){
							case SystemFilter.ALL:
								tc = s.ticket_cost;
								cm = s.commission;
								break;
							case SystemFilter.T2:
								tc = s.legacy.ticket_cost;
								cm = s.legacy.commission;
								break;
							case SystemFilter.T3:
								tc = s.ticket_cost-s.legacy.ticket_cost;
								cm = s.commission-s.legacy.commission;
								break;
						}
	
						business[bx].ticket_cost += tc;
						business[bx].commission += cm;
						sources[sx].ticket_cost += tc;
						sources[sx].commission += cm;
						totals.ticket_cost += tc;
						totals.commission += cm;
					}
				}
			}
		}
		return { sources, business, totals };
	}, [data, eventSearch, businessFilter, systemFilter]);

	if(!!loadError) return <Header text={t('common.error')} subtext={loadError} />
	if(!data) return <Header text={t('common.loading')} loading />

	return <div style={{ maxWidth: 800, margin: 'auto' }}>
		<Header text='Papeleta Origenes' subtext={date_start.toFormat(props.month ? 'MMM/yyyy' : 'dd/MMM/yyyy')} style={{ marginBottom: 15 }} />
		<Field amount={3}>
			<Input fluid label='Buscar evento' value={eventSearch} onChange={setEventSearch} />
			<Dropdown label='Negocio' value={businessFilter} onAsyncSearch={null} style={{ width: '33%' }} onValueChange={setBusinessFilter} items={[
				{ value: -1, text: 'Todos' },
				...business.map(a=>({
					value: a.business_id,
					text: a.business_name,
				}))
			]} />
			<Dropdown label='Sistema' value={systemFilter} style={{ width: '33%' }} onValueChange={v=>setSystemFilter(v)} items={[
				{ value: SystemFilter.ALL, text: 'Todos' },
				{ value: SystemFilter.T3, text: 'Solo T3' },
				{ value: SystemFilter.T2, text: 'Solo T2' },
			]} />
		</Field>
		<Table>
			<Table.Head title='Eventos' data={[
				['ID', 'Evento', 'Promotor', 'Cargo servicio', 'Total']
			]} collapsingIndexes={[0, 2, 3, 4]} />
			<Table.Body>
				{data_summary?.map(a=>(
					<Table.Row key={`SMR${a.event_id}`} collapsingIndexes={[0, 2, 3, 4]} data={[
						a.event_id,
						a.event_name,
						<>
							<span className="ar money-sign">$</span>
							{addCommas(a.ticket_cost)}
						</>,
						<>
							<span className="ar money-sign">$</span>
							{addCommas(a.commission)}
						</>,
						<>
							<span className="ar money-sign">$</span>
							{addCommas(a.ticket_cost+a.commission)}
						</>,
					]} />
				))}
			</Table.Body>
			<Table.Footer>
				<Table.Row collapsingIndexes={[0, 2, 3]} header>
					<Table.Cell colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</Table.Cell>
					<Table.Cell>
						<span className="ar money-sign">$</span>
						{addCommas(totals.commission)}
					</Table.Cell>
					<Table.Cell>
						<span className="ar money-sign">$</span>
						{addCommas(totals.ticket_cost)}
					</Table.Cell>
					<Table.Cell>
						<span className="ar money-sign">$</span>
						{addCommas(totals.commission+totals.ticket_cost)}
					</Table.Cell>
				</Table.Row>
			</Table.Footer>
		</Table>

		<Table style={{ marginTop: 15 }}>
			<Table.Head 
				title='Origenes'
				collapsingIndexes={[0, 2, 3, 4]}
				data={[['ID', 'Origen', 'Cargo servicio', 'Boletos', 'Total']]}
			/>
			<Table.Body>
				{sources?.map(a=>(
					<Table.Row key={`SOUR-${a.source_id}`} collapsingIndexes={[0, 2, 3, 4]} data={[
						a.source_id || <Icon name='minus' color='gray' />,
						a.source_name || <i style={{ color: 'gray' }}>Sin registro</i>,
						<>
							<span className="ar money-sign">$</span>
							{addCommas(a.commission)}
						</>,
						<>
							<span className="ar money-sign">$</span>
							{addCommas(a.ticket_cost)}
						</>,
						<>
							<span className="ar money-sign">$</span>
							{addCommas(a.ticket_cost+a.commission)}
						</>,
					]} />
				))}
			</Table.Body>
			<Table.Footer>
				<Table.Row collapsingIndexes={[0, 2, 3, 4]} header>
					<Table.Cell colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</Table.Cell>
					<Table.Cell>
						<span className="ar money-sign">$</span>
						{addCommas(totals.commission)}
					</Table.Cell>
					<Table.Cell>
						<span className="ar money-sign">$</span>
						{addCommas(totals.ticket_cost)}
					</Table.Cell>
					<Table.Cell>
						<span className="ar money-sign">$</span>
						{addCommas(totals.commission+totals.ticket_cost)}
					</Table.Cell>
				</Table.Row>
			</Table.Footer>
		</Table>
		<Table style={{ marginTop: 15 }}>
			<Table.Head 
				title='Negocios'
				collapsingIndexes={[0, 2, 3, 4]}
				data={[['ID', 'Negocio', 'Cargo servicio', 'Boletos', 'Total']]}
			/>
			<Table.Body>
				{business?.map(a=>(
					<Table.Row key={`BSN-${a.business_id}`} collapsingIndexes={[0, 2, 3, 4]} data={[
						a.business_id || <Icon name='minus' color='gray' />,
						a.business_name || <i style={{ color: 'gray' }}>Sin registro</i>,
						<>
							<span className="ar money-sign">$</span>
							{addCommas(a.commission)}
						</>,
						<>
							<span className="ar money-sign">$</span>
							{addCommas(a.ticket_cost)}
						</>,
						<>
							<span className="ar money-sign">$</span>
							{addCommas(a.ticket_cost+a.commission)}
						</>,
					]} />
				))}
			</Table.Body>
			<Table.Footer>
				<Table.Row collapsingIndexes={[0, 2, 3, 4]} header>
					<Table.Cell colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</Table.Cell>
					<Table.Cell>
						<span className="ar money-sign">$</span>
						{addCommas(totals.commission)}
					</Table.Cell>
					<Table.Cell>
						<span className="ar money-sign">$</span>
						{addCommas(totals.ticket_cost)}
					</Table.Cell>
					<Table.Cell>
						<span className="ar money-sign">$</span>
						{addCommas(totals.commission+totals.ticket_cost)}
					</Table.Cell>
				</Table.Row>
			</Table.Footer>
		</Table>
	</div>
}

export default OriginsDay;